/**
 * The explorer's **themes** — S3's rail, read off `/meta/config.theme_defaults`.
 *
 * Phase A put the client's own table on the server (`apps/meta/themes.py`) precisely so
 * that phase B would not restate it: the rail's order, which dataset each theme opens on,
 * which themes are provisional or in preparation, and which one is gated all arrive in one
 * payload and are resolved there against the catalogue. **Nothing in this module hardcodes
 * a theme key, a layer id or a flag.** A client revision is a server change.
 *
 * ## A theme is the rail's selection, and it is `domFilter`
 *
 * The eight §C1 domains were already the rail's chips and already wrote `domFilter`; S3's
 * "theme" is that same pick with two pseudo-entries either side — `all` (the empty string
 * in the store, which is what "no domain filter" has always been) and `indicators`, which
 * no `domains` row backs. So the store field is unchanged and the two pseudo-keys are
 * translated at this boundary rather than leaking into `lib/state.ts`.
 *
 * ## What changed about it
 *
 * Until S3 the rail *filtered lists and never touched the map* (§F5.2, and the rail's own
 * header said so). S3 makes selecting a theme select its default dataset — the client's
 * table is a table of defaults, not of filters — so the pick now has an effect on the map.
 * That is a deliberate departure from §F5.2 and is recorded in docs/NOTES.md.
 */
import type { BaselineTheme, Layer, MetaConfig, ThemeDefault } from '../../lib/api';
import { openAccess } from '../../lib/access';

/** The rail's first chip. `''` in the store — see the module docstring. */
export const ALL_THEME = 'all';

/** The rail's last chip, after the baseline band. Backed by no `domains` row. */
export const INDICATORS_THEME = 'indicators';

/**
 * Key prefix for the **2025 MELS baseline** sheets — the rail's third kind of entry.
 *
 * Client directive, 8 Sep 2026: the baseline came with themes of its own and needs chips of
 * its own, *told apart* from the eight domain chips and from the Indicators icon. A baseline
 * key is still a theme key and still `domFilter` — nothing about the store changed — so
 * everything under the rail (`themeEntry`, `themeDefaults`, `applyThemeDefault`, the panel)
 * treats one exactly as it treats `soil`. The prefix is what the **rail** branches on to
 * give the band its divider, its micro-label and its own chip treatment.
 */
export const BASELINE_PREFIX = 'bl_';

/** Is this theme key one of the 2025 baseline sheets? */
export function isBaselineTheme(theme: string): boolean {
  return theme.startsWith(BASELINE_PREFIX);
}

/** The baseline sheets in the server's order, or `[]` while `/meta/config` is unanswered. */
export function baselineThemes(config: MetaConfig | undefined): BaselineTheme[] {
  return config?.theme_defaults?.baseline_themes ?? [];
}

/** One baseline sheet's row by theme key, or `null`. */
export function baselineTheme(
  config: MetaConfig | undefined,
  theme: string,
): BaselineTheme | null {
  return baselineThemes(config).find((row) => row.key === theme) ?? null;
}

/**
 * The two-letter monogram a baseline chip prints in place of a domain's icon glyph.
 *
 * A monogram rather than an icon because ten more pictograms on one rail is ten more things
 * to learn, and because letters are visibly *not the same kind of mark* as the eight domain
 * glyphs — which is half the distinction the client asked for.
 *
 * **The server sends the mark** (`BaselineTheme.monogram`) and this is the fallback for a
 * sheet it has none for. Written that way round because initials chosen by rule collide:
 * *Agroforestry* and *Agroecology* both begin "Ag", and so do their French forms, as do
 * *Inputs* and *Income*. Deriving is fine for a sheet nobody has named yet and wrong as the
 * general rule, so it is what happens when the table has no answer, not instead of asking it.
 */
export function monogramOf(short: string): string {
  // Whitespace and hyphens break words; an apostrophe does not — "Women's land" is two words
  // and its initials are WL, not WS.
  const words = short.trim().split(/[\s\-‐-―]+/u).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
  }
  return (words[0] ?? short).replace(/['’]/gu, '').slice(0, 2).toUpperCase();
}

/** `domFilter` → theme key. The empty string is All. */
export function themeOf(domFilter: string): string {
  return domFilter || ALL_THEME;
}

/** Theme key → `domFilter`. The inverse of `themeOf`. */
export function domFilterOf(theme: string): string {
  return theme === ALL_THEME ? '' : theme;
}

/** The rail's order, or an empty list while `/meta/config` has not answered. */
export function themeOrder(config: MetaConfig | undefined): string[] {
  return config?.theme_defaults?.order ?? [];
}

/** One theme's row, or `null` — absent is "the server has not said", never "no defaults". */
export function themeEntry(
  config: MetaConfig | undefined,
  theme: string,
): ThemeDefault | null {
  return config?.theme_defaults?.themes?.[theme] ?? null;
}

/**
 * Is this theme reachable for this role?
 *
 * `requires_signin` is the flag the server sets on Indicators, and the rule it stands for
 * is the client's: *programme personas only*. `public` is the §I1 code for a guest, and
 * every other role is somebody who signed in — so this is one comparison rather than a
 * second copy of the role ladder. §I1 still enforces the data; this is the §I2 mirror.
 *
 * `open` short-circuits it, and it is the caller's `openAccess(config)`. The server's row
 * still carries `requires_signin: true` — the client's table is unchanged and the flag is
 * what the later permissions task will switch back on — so the bypass lives here, in the one
 * function the chip, the panel and the permalink all gate through, rather than in a rewrite
 * of the table.
 */
export function themeAllowed(
  entry: ThemeDefault | null,
  role: string,
  open = false,
): boolean {
  return open || !entry?.requires_signin || role !== 'public';
}

/**
 * Is **programme information** open to this reader? — `themeAllowed` for the Indicators
 * theme, in one call, so two surfaces cannot answer it differently.
 *
 * The second surface is the S2 Overview's Dashboard action (client, 8 Sep 2026: *"Guest
 * logins cannot see the Dashboard because its SV Programme Information"*). That is the same
 * sentence the server already writes as `requires_signin` on the Indicators row, and the same
 * flag — `features.open_access` — stands both of them down while the read surface is open. So
 * the Overview does not get a gate of its own; it asks this, and gets the rail's answer.
 *
 * The **row is still the server's**: nothing here hardcodes "guests are out", it reads
 * whatever `/meta/config` says about the theme that carries the flag.
 */
export function programmeInfoAllowed(config: MetaConfig | undefined, role: string): boolean {
  return themeAllowed(themeEntry(config, INDICATORS_THEME), role, openAccess(config));
}

/**
 * The indicator ids the explorer still shows **outside** the Indicators icon.
 *
 * S3 moves contractual and non-contractual indicators behind the icon but keeps the
 * **baseline variables** on the explorer as ordinary datasets — GESI's extension pair,
 * SLM's twelve crop yields — because the client's own default table names them as what
 * those themes open on. Both kinds land in the same store slot (`applyThemeDefault` calls
 * `setIndicator` for either), so "is this id still an explorer dataset?" cannot be asked of
 * the store; it has to be asked of the table.
 *
 * The union of every theme's `indicators` list **is** that answer, and taking it from the
 * server means a client revision that adds a baseline variable does not also need a change
 * here. An id absent from the union is one the icon owns.
 *
 * Returns an empty set while `/meta/config` is unanswered — callers must treat that as
 * "not known yet" rather than "nothing is allowed", or a slow config would read as a
 * portal that hides its own defaults.
 */
export function explorerVariableIds(config: MetaConfig | undefined): Set<string> {
  const themes = config?.theme_defaults?.themes ?? {};
  const out = new Set<string>();
  for (const entry of Object.values(themes)) {
    for (const id of entry?.indicators ?? []) out.add(id);
  }
  return out;
}

/**
 * The datasets a theme opens on, split the way the panel renders them.
 *
 * * `layers` — §D5 rows, in the catalogue order the server resolved (already dropped of
 *   any id the catalogue has no row for).
 * * `variables` — §C indicator ids the theme names. Only the MELS **baseline variables**
 *   appear there: S3 moves contractual and thematic indicators behind the Indicators icon,
 *   and a baseline variable is a survey measurement the client's own table lists as a
 *   theme's default. Naming them in the config is what keeps that distinction the server's
 *   to make — and since the 8 Sep directive it is also what keeps the eight domains clear of
 *   the baseline: only GESI and SLM still name one, a baseline chip names its whole sheet,
 *   and All names every sheet (a baseline variable is a dataset).
 */
export function themeDefaults(entry: ThemeDefault | null): {
  layers: string[];
  variables: string[];
} {
  return { layers: entry?.layers ?? [], variables: entry?.indicators ?? [] };
}

/**
 * The **one** dataset a theme auto-selects, or `null`.
 *
 * The first layer the server resolved, else the first baseline variable, else nothing —
 * which is the honest answer for All (no default: it is the whole catalogue) and for
 * Digital Agriculture (`in_preparation`, and the client asked for no dataset at all).
 * A `display_only` theme still has one: Ecosystem opens on its GIBS layer with its note,
 * because "cannot be analysed" is not "cannot be shown".
 */
export function themeDefaultDataset(
  entry: ThemeDefault | null,
): { kind: 'layer' | 'variable'; id: string } | null {
  if (!entry || entry.in_preparation) return null;
  if (entry.layers.length) return { kind: 'layer', id: entry.layers[0] as string };
  if (entry.indicators.length) return { kind: 'variable', id: entry.indicators[0] as string };
  return null;
}

/**
 * What a theme's panel is **able to offer** — the client's 9 Sep 2026 first directive.
 *
 * > *"Selecting a baseline theme renders the layers-pane furniture — Raster dataset / Select
 * > dataset / Vector datasets — which is meaningless for survey variables."*
 *
 * The pane used to render both §D5 selects unconditionally and let the option lists come out
 * empty. Under a baseline chip they always do: a sheet's row carries `layers: []`, and its key
 * (`bl_agroforestry`) is not a §C1 domain, so `layerInTheme` matches nothing in the catalogue.
 * The reader got two controls with one *"— Select dataset —"* placeholder each and no way to
 * tell a dead control from an empty one.
 *
 * **The fix is a capability, asked of the server's own row, not a `bl_` test.** Three facts
 * decide it and all three are in `/meta/config.theme_defaults`:
 *
 * * `in_preparation` — Digital Agriculture. The client asked for the state and no dataset, so
 *   the theme offers nothing at all and the pane prints its sentence instead of controls.
 * * the theme names §D5 `layers`, **or** its key is a §C1 domain — the two ways a theme can
 *   reach the catalogue, which is exactly `layerInTheme`'s own rule read one level up. `all`
 *   is the whole catalogue by definition.
 * * the theme names `indicators` — the MELS baseline variables it opens on.
 *
 * So a baseline sheet answers `{ layers: false, variables: true }` without this module
 * knowing what a baseline sheet is, and a domain that later gains a sheet's variables, or a
 * sheet that later gains a published raster, changes its own controls by changing its row.
 *
 * `config` rather than the catalogue: a §C1 domain is a `/meta/config.domains` row, which
 * lands with the same payload as the table. Deriving the answer from `['layers', role]`
 * instead would make the pane's shape depend on a second, slower query and flash the two
 * selects in and out on every theme change.
 */
export interface ThemeCapability {
  /** The pane offers the §D5 *Raster dataset* and *Vector datasets* selects. */
  layers: boolean;
  /** The pane offers the *Variables* select — the theme's MELS baseline datasets. */
  variables: boolean;
  /** Anything at all to pick, and therefore a *Filter datasets* box worth showing. */
  any: boolean;
}

/**
 * What the pane is looking at *right now*, where that changes what it may offer.
 *
 * One field, and it is the 9 Sep 2026 second directive: **no dataset furniture wherever a
 * choropleth is displayed**. A choropleth is a variable or an indicator painted on the
 * hierarchy, and the two §D5 selects under one are furniture for a different map — picking a
 * raster there would replace the very thing the panel below is describing.
 */
export interface ThemeContext {
  /** True while an indicator or a baseline variable is mapped as a choropleth. */
  choropleth?: boolean;
}

export function themeCapability(
  config: MetaConfig | undefined,
  theme: string,
  entry: ThemeDefault | null,
  { choropleth = false }: ThemeContext = {},
): ThemeCapability {
  // "No dataset has been published for this theme yet" is the whole answer — a filter box
  // over an empty catalogue is a control with nothing to do.
  if (entry?.in_preparation) return { layers: false, variables: false, any: false };
  const isDomain = (config?.domains ?? []).some((d) => d.id === theme);
  const variables = Boolean(entry?.indicators?.length);
  /**
   * **A choropleth takes the §D5 selects away, whatever the theme can reach** (client
   * directive, 9 Sep 2026):
   *
   * > *"Raster dataset / Select dataset / Vector datasets must not render wherever a
   * > choropleth is displayed."*
   *
   * Yesterday's gate asked what a theme *can* offer and stopped there, which was right for a
   * baseline sheet (it can reach no raster at all) and wrong for GESI and SLM: both are §C1
   * domains, so both keep `layers: true`, and both open on a survey variable — so the reader
   * got a raster select and a vector select over a choropleth of something else entirely.
   * The Indicators icon is the same case and was already covered by accident: it is not a
   * domain and names no layers.
   *
   * The controls come **back** the moment the variable is unmapped (the Variables select's
   * own placeholder does it), so nothing is unreachable — the pane simply describes one map
   * at a time.
   */
  const layers =
    !choropleth && (theme === ALL_THEME || isDomain || Boolean(entry?.layers?.length));
  return { layers, variables, any: layers || variables };
}

/**
 * The §D5 ids this theme's dataset selects must not list — the 9 Sep 2026 one-epoch switch.
 *
 * > *"hide all and leave the landcover 2024, to avoid confusion"*
 *
 * The list is the **server's** (`apps/meta/themes.py::HIDDEN_LAYERS`), so nothing here knows
 * that WorldCover exists and reversing the directive is a one-line change on the server. It
 * is a filter on the *options*, never on the map: a hidden row that is somehow already drawn
 * stays drawn and stays named in its own select (`PaneSelect`'s `fallback`), because a
 * control reading *None* over a drawn layer is a control lying about the map.
 */
export function themeHiddenLayers(entry: ThemeDefault | null): Set<string> {
  return new Set(entry?.hidden_layers ?? []);
}

/**
 * Is this series layer's **time slider** off? — the other half of the same switch.
 *
 * `lc_annual` is bound to the newest published year's pixels ("Land cover (Impact
 * Observatory, 10 m)" — client, 2026-09-15, the name itself carries no year), so a locked
 * slider leaves the explorer on the 2024 land cover at full extent with no way to step off
 * it, which is what the client asked for. The lock is on the *series* rather than on
 * Land Use because the slider follows the drawn raster: locking the theme would leave the
 * same eight years steppable from All.
 *
 * Nothing else changes — `epochs` is still served, so the eight per-year rows stay out of the
 * datasets list, and §H5's change tool still addresses them by id.
 */
export function seriesLocked(config: MetaConfig | undefined, seriesId: string): boolean {
  return (config?.theme_defaults?.locked_series ?? []).includes(seriesId);
}

/**
 * Which of the theme's states the panel should print, or `null` for an ordinary theme.
 *
 * One key rather than three booleans on screen, because the three are mutually exclusive
 * in the client's table and a panel showing two of them at once would be describing a
 * theme the server cannot serve. The order is the order they matter in: a theme with no
 * dataset says so before it says anything about the datasets it does not have.
 */
export type ThemeState = 'in_preparation' | 'provisional' | 'display_only';

export function themeState(entry: ThemeDefault | null): ThemeState | null {
  if (!entry) return null;
  if (entry.in_preparation) return 'in_preparation';
  if (entry.provisional) return 'provisional';
  if (entry.display_only) return 'display_only';
  return null;
}

/**
 * Does this layer belong in `theme`'s dataset list?
 *
 * The domain match is what the rail has always done. The theme's own `layers` are unioned
 * onto it so a default the client named can never be filtered out of the list it is the
 * default of — the water rows are all `water`-domain today, but the table is the client's
 * and the two are not required to agree forever.
 */
export function layerInTheme(layer: Layer, theme: string, entry: ThemeDefault | null): boolean {
  // Task 6 §6.4: an intelligence-only raster (`config.internal`, e.g. `intel_uc1_overlap`)
  // is never a theme's dataset — it stays reachable for tiles and `setActiveRaster`, just
  // not as a row a reader finds by browsing a domain.
  if (layer.internal) return false;
  if (theme === ALL_THEME) return true;
  return layer.domain === theme || (entry?.layers ?? []).includes(layer.id);
}

/**
 * The theme a cold explorer opens on: the first in the rail's order whose default is a
 * published **series**.
 *
 * Derived rather than named. S3's default view is "the latest land cover, with the series
 * behind a slider", and `series` is precisely the field that says a theme has one — so the
 * clause and the lookup are the same sentence. `null` when the table has no series theme,
 * which is a stack whose land-cover harvest has not run: then nothing is selected and the
 * explorer opens on the map alone, which is the honest picture.
 */
export function defaultViewTheme(config: MetaConfig | undefined): string | null {
  const themes = config?.theme_defaults?.themes;
  if (!themes) return null;
  for (const key of themeOrder(config)) {
    if (key === ALL_THEME || key === INDICATORS_THEME) continue;
    const entry = themes[key];
    if (entry?.series && entry.layers.length) return key;
  }
  return null;
}
